import React, { useState } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { API_URL } from '../../../config/api';
import styles from './ActionModals.module.scss';

/**
 * 🎫 CREATE TICKET MODAL - Modal para Criar Ticket de Suporte
 * 
 * @param {boolean} show - Controla visibilidade
 * @param {Function} onHide - Callback ao fechar
 * @param {number} saleId - ID da venda
 * @param {Object} sale - Dados da venda
 * @param {Function} onSuccess - Callback após sucesso
 */
const CreateTicketModal = ({ show, onHide, saleId, sale, onSuccess }) => {
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    category: 'general',
    priority: 'normal'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const categories = [
    { value: 'technical', label: 'Técnico', icon: 'mdi:wrench', color: '#3b82f6' },
    { value: 'financial', label: 'Financeiro', icon: 'mdi:currency-usd', color: '#10b981' },
    { value: 'administrative', label: 'Administrativo', icon: 'mdi:account-supervisor', color: '#8b5cf6' },
    { value: 'general', label: 'Geral', icon: 'mdi:help-circle', color: '#6b7280' }
  ];

  const priorities = [
    { value: 'low', label: 'Baixa', color: '#10b981' },
    { value: 'normal', label: 'Normal', color: '#3b82f6' },
    { value: 'high', label: 'Alta', color: '#f59e0b' },
    { value: 'urgent', label: 'Urgente', color: '#ef4444' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.subject.trim() || !formData.description.trim()) {
      setError('Preencha o assunto e a descrição');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/sales/${saleId}/create-ticket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao criar ticket');
      }

      console.log('✅ Ticket criado:', data.ticket);

      // Reset e fechar
      setFormData({
        subject: '',
        description: '',
        category: 'general',
        priority: 'normal'
      });
      onHide();
      
      if (onSuccess) {
        onSuccess();
      }

    } catch (err) {
      console.error('❌ Erro ao criar ticket:', err);
      setError(err.message || 'Erro ao criar ticket de suporte');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      backdrop={false}
      className={styles.actionModal}
      enforceFocus={false}
    >
      <Modal.Header closeButton className={styles.headerTicket}>
        <Modal.Title as="span" className={styles.modalTitle}>
          <Icon icon="mdi:ticket-confirmation" />
          Criar Ticket de Suporte
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body className={styles.modalBody}>
          {error && (
            <Alert variant="danger" className="mb-3">
              <Icon icon="mdi:alert-circle" className="me-2" />
              {error}
            </Alert>
          )}

          {/* Info da Venda */}
          <Alert variant="info" className="mb-3">
            <div className="d-flex align-items-center gap-2">
              <Icon icon="mdi:cart" style={{ fontSize: '1.5rem' }} />
              <div>
                <strong>Venda #{sale?.id}</strong><br/>
                <small>{sale?.product_name} - {sale?.customer_name}</small>
              </div>
            </div>
          </Alert>

          {/* Categoria */}
          <Form.Group className="mb-3">
            <Form.Label>
              <Icon icon="mdi:tag" className="me-2" />
              Categoria *
            </Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {categories.map(cat => (
                <button
                  key={cat.value}
                  type="button"
                  className={`btn btn-sm ${formData.category === cat.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => handleChange('category', cat.value)}
                  disabled={loading}
                >
                  <Icon icon={cat.icon} className="me-1" />
                  {cat.label}
                </button>
              ))}
            </div>
          </Form.Group>

          {/* Prioridade */}
          <Form.Group className="mb-3">
            <Form.Label>
              <Icon icon="mdi:flag" className="me-2" />
              Prioridade *
            </Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {priorities.map(pri => (
                <button
                  key={pri.value}
                  type="button"
                  className={`btn btn-sm ${formData.priority === pri.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => handleChange('priority', pri.value)}
                  disabled={loading}
                  style={formData.priority === pri.value ? { borderColor: pri.color, backgroundColor: pri.color } : {}}
                >
                  {pri.label}
                </button>
              ))}
            </div>
          </Form.Group>

          {/* Assunto */}
          <Form.Group className="mb-3">
            <Form.Label>
              <Icon icon="mdi:format-title" className="me-2" />
              Assunto *
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Digite o assunto do ticket..."
              value={formData.subject}
              onChange={(e) => handleChange('subject', e.target.value)}
              disabled={loading}
              required
            />
          </Form.Group>

          {/* Descrição */}
          <Form.Group>
            <Form.Label>
              <Icon icon="mdi:text" className="me-2" />
              Descrição *
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              placeholder="Descreva o problema ou solicitação em detalhes..."
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              disabled={loading}
              required
            />
            <Form.Text className="text-muted">
              Os detalhes da venda serão anexados automaticamente ao ticket
            </Form.Text>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className={styles.modalFooter}>
          <Button
            variant="outline-secondary"
            onClick={onHide}
            disabled={loading}
          >
            <Icon icon="mdi:close" className="me-2" />
            Cancelar
          </Button>
          
          <Button
            variant="warning"
            type="submit"
            disabled={loading || !formData.subject.trim() || !formData.description.trim()}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Criando...
              </>
            ) : (
              <>
                <Icon icon="mdi:ticket-plus" className="me-2" />
                Criar Ticket
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateTicketModal;

